// src/lib/firestore-instrumented.js

import {
  // reads / listeners
  getDoc as _getDoc,
  getDocs as _getDocs,
  onSnapshot as _onSnapshot,
  // builders
  doc, collection, collectionGroup, query, where, orderBy, limit, startAfter, startAt, endAt, serverTimestamp, documentId,
  // writes / utils
  updateDoc as _updateDoc,
  setDoc as _setDoc,
  addDoc as _addDoc,
  deleteDoc as _deleteDoc,
  deleteField,
  getCountFromServer as _getCountFromServer,
  writeBatch as _writeBatch,
} from "firebase/firestore";

// Always import the single, initialized Firestore instance from your config
export { db } from "@/firebase/config";
export { query, where, orderBy, limit, startAfter, startAt, endAt, doc, collection, collectionGroup, serverTimestamp, documentId, deleteField };

import { incFsReads } from "./fsReads";
import { sanitizeFirestorePath } from "./firestoreDebug.mjs";

const DEBUG = (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production");

/* ---------- in-memory log buffer + helpers ---------- */
const __fsLog = []; // { t, ts, kind, label, path, size, open, durationMs, note }
const __queryLabels = new WeakMap();
let __openListens = 0;
const MAX_LOG_ENTRIES = (() => {
  try {
    const raw = typeof process !== "undefined" ? process?.env?.NEXT_PUBLIC_FS_LOG_MAX : null;
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
  } catch {
    return 5000;
  }
})();

function nowISO() { return new Date().toISOString(); }
function nowMs() {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {}
  return Date.now();
}
function durationSince(startedAt) {
  return Math.max(0, Math.round((nowMs() - startedAt) * 10) / 10);
}
function appendBounded(buffer, entry) {
  buffer.push(entry);
  if (buffer.length > MAX_LOG_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_LOG_ENTRIES);
  }
}
function shouldBufferLogs() {
  if (DEBUG) return true;
  try {
    if (typeof window !== "undefined") return isFsDebugEnabled();
  } catch {}
  try {
    const envFlag = typeof process !== "undefined" ? process?.env?.NEXT_PUBLIC_FS_DEBUG : null;
    return envFlag === "1";
  } catch {}
  return false;
}
function pushLog(entry) {
  if (!shouldBufferLogs()) return;
  const kind = entry.kind || entry.type || "unknown";
  const e = { t: Date.now(), ts: nowISO(), ...entry, kind };
  appendBounded(__fsLog, e);
  try {
    if (typeof window !== "undefined" && isFsDebugEnabled()) {
      appendBounded(_fslogBuffer(), e);
    }
  } catch {}
  if (DEBUG) console.debug("[FSLOG]", e);
}

// --- Debug flag helpers (no-ops in production unless you flip the switch) ---
export function isFsDebugEnabled() {
  try {
    if (typeof window === "undefined") return false;
    const fromParam = new URLSearchParams(window.location.search).get("fsdebug");
    if (fromParam === "1") { localStorage.setItem("fsDebug", "1"); }
    if (fromParam === "0") { localStorage.removeItem("fsDebug"); }
    return localStorage.getItem("fsDebug") === "1" || (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_FS_DEBUG === "1");
  } catch { return false; }
}

export function setFsDebugEnabled(v) {
  try {
    if (v) localStorage.setItem("fsDebug", "1");
    else localStorage.removeItem("fsDebug");
  } catch {}
}

function _fslogBuffer() {
  if (typeof window === "undefined") return [];
  window.__fslog = window.__fslog || [];
  return window.__fslog;
}

// Call this anywhere you already log Firestore activity
export function fsDebugPush(entry) {
  pushLog(entry);
}

export function getOpenListenCount() { return __openListens; }
export function getFsLog() { return __fsLog.slice(); }
export function clearFsLog() {
  __fsLog.length = 0;
  try {
    if (typeof window !== "undefined" && Array.isArray(window.__fslog)) {
      window.__fslog.length = 0;
    }
  } catch {}
}

/** Download the log as a JSON file */
export function downloadFsLog(filename = `firestore-log-${new Date().toISOString()}.json`) {
  try {
    if (typeof window === "undefined") return; // SSR guard
    const events = Array.isArray(window.__fslog) ? window.__fslog : __fsLog;
    const blob = new Blob([JSON.stringify(events, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.warn("[FSLOG] download failed", e);
  }
}

/* ---------- helpers for readable labels ---------- */
function refPath(ref) {
  try {
    if (!ref) return "[unknown-ref]";
    if (typeof ref.path === "string") return sanitizeFirestorePath(ref.path); // DocumentReference
    // Query objects may not expose path; prefer label
    if (ref.firestore && (ref.type === "query" || ref._query || ref._constraint)) return "[query]";
    return String(ref);
  } catch { return String(ref); }
}
function qLabel(q) {
  if (!q || (typeof q !== "object" && typeof q !== "function")) return "[query]";
  return __queryLabels.get(q) || q.__label || "[query]";
}
function snapshotSource(snap) {
  return snap?.metadata?.fromCache ? "cache" : "server";
}

/* ------------------- READS ------------------- */
export async function getDoc(ref) {
  const p = refPath(ref);
  const startedAt = nowMs();
  try {
    const snap = await _getDoc(ref);
    incFsReads(1);
    pushLog({
      kind: "read:getDoc",
      path: p,
      size: snap.exists() ? 1 : 0,
      durationMs: durationSince(startedAt),
      status: "success",
      source: snapshotSource(snap),
      note: `exists=${snap.exists()}`,
    });
    return snap;
  } catch (err) {
    pushLog({
      kind: "read:getDoc",
      path: p,
      size: 0,
      durationMs: durationSince(startedAt),
      status: "error",
      note: err?.code || err?.name || "error",
    });
    throw err;
  }
}

export async function getDocs(q) {
  const label = qLabel(q);
  const startedAt = nowMs();
  try {
    const snap = await _getDocs(q);
    incFsReads(snap.size);
    pushLog({
      kind: "read:getDocs",
      label,
      size: snap.size,
      durationMs: durationSince(startedAt),
      status: "success",
      source: snapshotSource(snap),
    });
    return snap;
  } catch (err) {
    pushLog({
      kind: "read:getDocs",
      label,
      size: 0,
      durationMs: durationSince(startedAt),
      status: "error",
      note: err?.code || err?.name || "error",
    });
    throw err;
  }
}

export function onSnapshot(refOrQuery, nextOrObserver, error, complete) {
  let initialized = false;
  let stopped = false;
  const label = refOrQuery?.path ? refPath(refOrQuery) : qLabel(refOrQuery);
  const startedAt = nowMs();

  __openListens++;
  pushLog({ kind: "listen:start", label, open: __openListens });

  const markStopped = (reason) => {
    if (stopped) return;
    stopped = true;
    __openListens = Math.max(0, __openListens - 1);
    pushLog({
      kind: "listen:stop",
      label,
      open: __openListens,
      durationMs: durationSince(startedAt),
      note: reason,
    });
  };

  const unsub = _onSnapshot(
    refOrQuery,
    (snap) => {
      if (!initialized) {
        const initCount = typeof snap.size === "number"
          ? snap.size
          : (typeof snap.exists === "function" && snap.exists() ? 1 : 0);
        incFsReads(initCount);
        pushLog({
          kind: "listen:init",
          label,
          size: initCount,
          open: __openListens,
          durationMs: durationSince(startedAt),
          source: snapshotSource(snap),
        });
        initialized = true;
      } else {
        let delta = 1;
        try {
          if (typeof snap.docChanges === "function") {
            const changes = snap.docChanges();
            if (Array.isArray(changes)) delta = changes.length;
          }
        } catch {}
        incFsReads(delta);
        pushLog({
          kind: "listen:delta",
          label,
          size: delta,
          open: __openListens,
          source: snapshotSource(snap),
        });
      }

      if (typeof nextOrObserver === "function") nextOrObserver(snap);
      else if (nextOrObserver?.next) nextOrObserver.next(snap);
    },
    (err) => {
      pushLog({
        kind: "listen:error",
        label,
        open: __openListens,
        durationMs: durationSince(startedAt),
        note: err?.code || err?.name || "error",
      });
      markStopped("error");
      if (typeof error === "function") error(err);
      else if (nextOrObserver?.error) nextOrObserver.error(err);
    },
    () => {
      markStopped("complete");
      if (typeof complete === "function") complete();
      else if (nextOrObserver?.complete) nextOrObserver.complete();
    }
  );

  return () => {
    try { unsub(); } finally {
      markStopped("unsubscribe");
    }
  };
}

/* ------------------- WRITES / UTILS ------------------- */
export async function updateDoc(ref, data, ...rest) {
  pushLog({ kind: "write:updateDoc", path: refPath(ref) });
  return _updateDoc(ref, data, ...rest);
}

export async function setDoc(ref, data, options) {
  pushLog({ kind: "write:setDoc", path: refPath(ref), note: options ? "merge?" : undefined });
  return _setDoc(ref, data, options);
}

export async function addDoc(colRef, data) {
  pushLog({ kind: "write:addDoc", path: refPath(colRef) });
  return _addDoc(colRef, data);
}

export function writeBatch(dbInstance) {
  pushLog({ kind: "write:batch", note: "init" });
  return _writeBatch(dbInstance);
}

export async function deleteDoc(ref) {
  pushLog({ kind: "write:deleteDoc", path: refPath(ref) });
  return _deleteDoc(ref);
}

export async function getCountFromServer(refOrQuery) {
  const label = qLabel(refOrQuery);
  const startedAt = nowMs();
  try {
    const result = await _getCountFromServer(refOrQuery);
    pushLog({
      kind: "read:getCountFromServer",
      label,
      size: 1,
      durationMs: durationSince(startedAt),
      status: "success",
    });
    return result;
  } catch (err) {
    pushLog({
      kind: "read:getCountFromServer",
      label,
      size: 0,
      durationMs: durationSince(startedAt),
      status: "error",
      note: err?.code || err?.name || "error",
    });
    throw err;
  }
}

/* Label helper you’re already using */
export function withLabel(q, label) {
  if (q && (typeof q === "object" || typeof q === "function")) {
    __queryLabels.set(q, label);
  }
  try { Object.defineProperty(q, "__label", { value: label }); } catch {}
  return q;
}
